import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Package, Download, RotateCcw, Eye, Search, ChevronRight, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

import {
    fetchItemCategoryDetailsAll,
    fetchMajorGroupCode,
    fetchItemCodesGridDetail,
    fetchAllItemCodes,
    resetSelectedCategoryData,
    clearError,
    selectItemCategoryDetails,
    selectMajorGroupCode,
    selectItemCodesGridDetail,
    selectLoading,
    selectErrors,
} from '../../slices/stockSlice/ItemCodeReportSlice';
import {
    SELECT_ALL,
    categoryNameOf,
    isSelectAllCategory,
    validateItemCodeFilters,
    buildItemCodeRequest,
    filterItemCodes,
    toExcelRows,
} from './itemCodeReportUtils';

// View Item Codes — legacy Reports/ViewItemCodes (+ ViewItemCodesGrid partial).
// First load shows every approved item code (legacy did the same on page ready).

const selectCls = 'w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed';
const btnCls = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
const th = 'px-2 py-1.5 text-[11px] font-bold text-white uppercase tracking-wide whitespace-nowrap';
const td = 'px-2 py-1 text-[11px] text-gray-800 dark:text-gray-200 align-top';

const Label = ({ children }) => (
    <label className="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{children}</label>
);

const money = (v) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0);

const ItemCodeReportPage = () => {
    const dispatch = useDispatch();
    const categoryRes = useSelector(selectItemCategoryDetails);
    const majorRes    = useSelector(selectMajorGroupCode);
    const gridRes     = useSelector(selectItemCodesGridDetail);
    const loading     = useSelector(selectLoading);
    const errors      = useSelector(selectErrors);

    const [category,   setCategory]   = useState('');
    const [majorGroup, setMajorGroup] = useState('');
    const [search,     setSearch]     = useState('');
    const [formError,  setFormError]  = useState('');

    const categories  = useMemo(() => (Array.isArray(categoryRes?.Data) ? categoryRes.Data : []), [categoryRes]);
    const majorGroups = Array.isArray(majorRes?.Data) ? majorRes.Data : [];
    const rows        = useMemo(() => (Array.isArray(gridRes?.Data) ? gridRes.Data : []), [gridRes]);
    const visibleRows = useMemo(() => filterItemCodes(rows, search), [rows, search]);

    useEffect(() => {
        dispatch(fetchItemCategoryDetailsAll());
        dispatch(fetchAllItemCodes());
    }, [dispatch]);

    useEffect(() => {
        Object.entries(errors).forEach(([key, err]) => {
            if (err) {
                toast.error(typeof err === 'string' ? err : 'Something went wrong loading item codes');
                dispatch(clearError({ errorType: key }));
            }
        });
    }, [errors, dispatch]);

    const handleCategoryChange = (value) => {
        setCategory(value);
        setMajorGroup('');
        setFormError('');
        dispatch(resetSelectedCategoryData());
        if (value && !isSelectAllCategory(value)) {
            const cat = categories.find(c => c.Categoryid === value);
            dispatch(fetchMajorGroupCode({ val: value, txt: categoryNameOf(cat?.CategoryValue) }));
        }
    };

    const handleView = () => {
        const msg = validateItemCodeFilters({ category, majorGroup });
        setFormError(msg);
        if (msg) return;
        const req = buildItemCodeRequest({ category, majorGroup });
        if (req.mode === 'all') dispatch(fetchAllItemCodes());
        else dispatch(fetchItemCodesGridDetail({ icc: req.icc, mgc: req.mgc }));
    };

    const handleReset = () => {
        setCategory('');
        setMajorGroup('');
        setSearch('');
        setFormError('');
        dispatch(resetSelectedCategoryData());
        dispatch(fetchAllItemCodes());
    };

    const handleExcel = () => {
        if (visibleRows.length === 0) { toast.warning('No data available to download'); return; }
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(toExcelRows(visibleRows)), 'Item Codes');
        XLSX.writeFile(wb, 'View Item Code Details Report.xlsx');
    };

    const gridLoading = loading.itemCodesGridDetail;

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm px-3 py-2.5 border border-gray-200 dark:border-gray-700">
                <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <span className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                        <Package className="h-4 w-4 text-white" />
                    </span>
                    View Item Codes
                </h1>
                <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                    <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">View Item Codes</span>
                </nav>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                    <div>
                        <Label>Item Category <span className="text-red-500">*</span></Label>
                        <select value={category} onChange={e => handleCategoryChange(e.target.value)}
                                disabled={loading.itemCategoryDetails} className={selectCls}>
                            <option value="">-Please Select-</option>
                            {categories.filter(c => c?.Categoryid).map(c => (
                                <option key={c.Categoryid} value={c.Categoryid}>{c.CategoryValue}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label>Major Group {category && !isSelectAllCategory(category) && <span className="text-red-500">*</span>}</Label>
                        <select value={majorGroup} onChange={e => { setMajorGroup(e.target.value); setFormError(''); }}
                                disabled={!category || isSelectAllCategory(category) || loading.majorGroupCode}
                                className={selectCls}>
                            <option value="">{loading.majorGroupCode ? 'Loading…' : '-Please Select-'}</option>
                            <option value={SELECT_ALL}>Select All</option>
                            {majorGroups.filter(g => g?.Majorgroupval).map(g => (
                                <option key={g.Majorgroupval} value={g.Majorgroupval}>{g.Majorgrouptext || g.Majorgroupval}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={handleView} disabled={gridLoading}
                                className={`${btnCls} bg-indigo-600 text-white hover:bg-indigo-700`}>
                            {gridLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} View
                        </button>
                        <button onClick={handleReset} disabled={gridLoading}
                                className={`${btnCls} bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600`}>
                            <RotateCcw className="h-3.5 w-3.5" /> Reset
                        </button>
                    </div>
                </div>
                {formError && (
                    <div className="mt-2 flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-[11px]">
                        <AlertTriangle className="h-3.5 w-3.5" /> {formError}
                    </div>
                )}
            </div>

            {/* Grid */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        {gridLoading ? 'Loading…' : `${visibleRows.length}${search ? ` of ${rows.length}` : ''} item code(s)`}
                    </span>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
                                   className="pl-7 pr-2 py-1 w-48 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <button onClick={handleExcel} disabled={visibleRows.length === 0}
                                className={`${btnCls} bg-green-600 text-white hover:bg-green-700`}>
                            <Download className="h-3.5 w-3.5" /> Excel
                        </button>
                    </div>
                </div>

                <div className="overflow-auto max-h-[65vh]">
                    <table className="min-w-full">
                        <thead className="bg-indigo-600 dark:bg-indigo-800 sticky top-0 z-10">
                            <tr>
                                <th className={`${th} text-center w-10`}>#</th>
                                <th className={`${th} text-center`}>Item Code</th>
                                <th className={`${th} text-left`}>Item Name</th>
                                <th className={`${th} text-left`}>Specification</th>
                                <th className={`${th} text-left`}>Units</th>
                                <th className={`${th} text-left`}>DCA</th>
                                <th className={`${th} text-right`}>Basic Price</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {visibleRows.map((r, i) => (
                                <tr key={`${r.ItemCode}-${i}`} className="hover:bg-indigo-50/50 dark:hover:bg-gray-700/50">
                                    <td className={`${td} text-center text-gray-400`}>{i + 1}</td>
                                    <td className={`${td} text-center font-medium whitespace-nowrap`}>{r.ItemCode}</td>
                                    <td className={`${td} min-w-[160px]`}>{r.Itemname}</td>
                                    <td className={`${td} min-w-[220px] whitespace-normal`}>{r.Specification}</td>
                                    <td className={`${td} whitespace-nowrap`}>{r.Units}</td>
                                    <td className={`${td} whitespace-nowrap`}>{r.ItemcodeDca}</td>
                                    <td className={`${td} text-right whitespace-nowrap`}>{money(r.Basicprice)}</td>
                                </tr>
                            ))}
                            {!gridLoading && visibleRows.length === 0 && (
                                <tr><td colSpan={7} className="py-8 text-center text-xs text-gray-400">No Data Found</td></tr>
                            )}
                            {gridLoading && visibleRows.length === 0 && (
                                <tr><td colSpan={7} className="py-8 text-center text-xs text-gray-400">
                                    <Loader2 className="inline h-4 w-4 animate-spin mr-1" />Loading item codes…
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ItemCodeReportPage;
